import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Section from "../components/Section";
import SectionHead from "../components/SectionHead";
import Button from "../components/Button";
import TaglineLine from "../components/TaglineLine";
import "./Service.css";

const Service: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  if (!slug) {
    return <div>Service not found</div>;
  }

  // Create dynamic translation key
  const serviceKey = `service.${slug}`;

  // Check if the service has translations
  const hasTranslations = t(`${serviceKey}.title`, { defaultValue: "" }) !== "";

  if (!hasTranslations) {
    return (
      <div className="service">
        <Section>
          <div className="container">
            <h1>Service Coming Soon</h1>
            <p>
              The detailed page for "{slug}" is currently under development.
              Please check back soon!
            </p>
            <Button variant="primary" href="/#services">
              Back to Services
            </Button>
          </div>
        </Section>
      </div>
    );
  }
  return (
    <div className="service">
      {/* Sub Hero */}
      <section
        className="sub-hero"
        style={
          {
            "--hero-bg": 'url("/img/photo-1450101499163-c8848c66ca85.jpg")',
          } as React.CSSProperties
        }
      >
        <div className="sub-hero-inner">
          <nav className="breadcrumb">
            <a href="/">{t("breadcrumb.home")}</a>
            <span className="sep">/</span>
            <a href="/#services">{t("breadcrumb.services")}</a>
            <span className="sep">/</span>
            <span className="current">{t(`${serviceKey}.title`)}</span>
          </nav>
          <div className="sub-hero-pill">
            <span className="num">{t(`${serviceKey}.num`).split(" ")[0]}</span>
            <span>{t(`${serviceKey}.eyebrow`)}</span>
          </div>
          <h1>{t(`${serviceKey}.heroTitle`)}</h1>
          <p className="sub-hero-blurb">{t(`${serviceKey}.heroBlurb`)}</p>
        </div>
      </section>

      {/* Overview */}
      <Section className="overview">
        <div className="overview-inner">
          <SectionHead
            eyebrow={t(`${serviceKey}.overview.eyebrow`)}
            title={t(`${serviceKey}.overview.title`)}
          />
          <p className="overview-first-letter">
            {t(`${serviceKey}.overview.p1`)}
          </p>
          <p>{t(`${serviceKey}.overview.p2`)}</p>
          <p>{t(`${serviceKey}.overview.p3`)}</p>
        </div>
      </Section>

      {/* Process 
      {i18n.exists(`${serviceKey}.process.title`) && (
        <Section alt>
          <SectionHead
            eyebrow={t(`${serviceKey}.process.eyebrow`)}
            title={t(`${serviceKey}.process.title`)}
            description={t(`${serviceKey}.process.desc`)}
          />
          <div className="process-grid">
            <div className="step">
              <div className="step-num"></div>
              <div className="step-body">
                <h3>{t(`${serviceKey}.process.step1.title`)}</h3>
                <p>{t(`${serviceKey}.process.step1.desc`)}</p>
              </div>
            </div>
            <div className="step">
              <div className="step-num"></div>
              <div className="step-body">
                <h3>{t(`${serviceKey}.process.step2.title`)}</h3>
                <p>{t(`${serviceKey}.process.step2.desc`)}</p>
              </div>
            </div>
            <div className="step">
              <div className="step-num"></div>
              <div className="step-body">
                <h3>{t(`${serviceKey}.process.step3.title`)}</h3>
                <p>{t(`${serviceKey}.process.step3.desc`)}</p>
              </div>
            </div>
            <div className="step">
              <div className="step-num"></div>
              <div className="step-body">
                <h3>{t(`${serviceKey}.process.step4.title`)}</h3>
                <p>{t(`${serviceKey}.process.step4.desc`)}</p>
              </div>
            </div>
            <div className="step">
              <div className="step-num"></div>
              <div className="step-body">
                <h3>{t(`${serviceKey}.process.step5.title`)}</h3>
                <p>{t(`${serviceKey}.process.step5.desc`)}</p>
              </div>
            </div>
          </div>
        </Section>
      )}
        */}

      {/* Outcomes */}
      {i18n.exists(`${serviceKey}.outcomes.title`) && (
        <Section alt>
          <SectionHead
            eyebrow={t(`${serviceKey}.outcomes.eyebrow`)}
            title={t(`${serviceKey}.outcomes.title`)}
            description={t(`${serviceKey}.outcomes.desc`)}
          />
          <div className="outcomes-grid">
            <div className="outcome">
              <div className="outcome-headline">
                {t(`${serviceKey}.outcomes.outcome1.headline`)}
              </div>
              <p>{t(`${serviceKey}.outcomes.outcome1.desc`)}</p>
            </div>
            <div className="outcome">
              <div className="outcome-headline">
                {t(`${serviceKey}.outcomes.outcome2.headline`)}
              </div>
              <p>{t(`${serviceKey}.outcomes.outcome2.desc`)}</p>
            </div>
            <div className="outcome">
              <div className="outcome-headline">
                {t(`${serviceKey}.outcomes.outcome3.headline`)}
              </div>
              <p>{t(`${serviceKey}.outcomes.outcome3.desc`)}</p>
            </div>
            <div className="outcome">
              <div className="outcome-headline">
                {t(`${serviceKey}.outcomes.outcome4.headline`)}
              </div>
              <p>{t(`${serviceKey}.outcomes.outcome4.desc`)}</p>
            </div>
          </div>
        </Section>
      )}

      {/* Who For
      {i18n.exists(`${serviceKey}.whoFor.eyebrow`) && (
        <section className="who-for">
          <div className="who-for-inner">
            <TaglineLine>
              <span>{t(`${serviceKey}.whoFor.eyebrow`)}</span>
            </TaglineLine>
            <h2>{t(`${serviceKey}.whoFor.title`)}</h2>
          </div>
        </section>
      )} */}

      {/* Related */}
      <Section alt={!i18n.exists(`${serviceKey}.outcomes.title`)}>
        <div className="related-inner">
          <h2>{t(`${serviceKey}.cta.title`)}</h2>
          <p>{t(`${serviceKey}.cta.desc`)}</p>
          <div className="related-actions">
            <Button variant="primary" href="/#contact">
              {t(`${serviceKey}.cta.button1`)}
            </Button>
            <Button variant="ghost" href="/#services">
              {t(`${serviceKey}.cta.button2`)}
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Service;
